import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ManagerMobileLayout from '../../components/ManagerMobileLayout'
import api from '../../lib/api'
import { useModules } from '../../lib/modules'
import SignalementModal from '../../components/SignalementModal'

export default function ManagerHome() {
  const nav = useNavigate()
  const [counts, setCounts] = useState({ pendingAbsences: 0, draftTimesheets: 0, total: 0 })
  const [clockStats, setClockStats] = useState({ presentCount: 0, breakCount: 0, lateCount: 0, shiftCount: 0 })
  const { modules, loaded } = useModules()
  // Signalement : le gérant choisit toujours le magasin (Cassis et Six-Fours séparés)
  const [estabs, setEstabs] = useState<{ id: string; name: string }[]>([])
  const [choixMagasin, setChoixMagasin] = useState(false)
  const [signalEstab, setSignalEstab] = useState<string | null>(null)
  const [signalOk, setSignalOk] = useState(false)
  useEffect(() => { api.get('/establishments').then(r => setEstabs(r.data || [])).catch(() => {}) }, [])
  const ouvrirSignalement = () => {
    setSignalOk(false)
    if (estabs.length === 1) setSignalEstab(estabs[0].id)
    else setChoixMagasin(true)
  }

  useEffect(() => {
    if (!loaded) return
    api.get('/notifications/counts').then(r => setCounts(r.data)).catch(() => {})
    if (!modules.badgeuse) return
    api.get('/clock/live').then(r => {
      const data: any[] = r.data
      setClockStats({
        presentCount: data.filter((e: any) => e.isPresent).length,
        breakCount: data.filter((e: any) => e.lastEvent === 'break').length,
        lateCount: data.filter((e: any) => e.lateMinutes && e.lateMinutes > 0 && e.lastEvent === 'arrive').length,
        shiftCount: data.filter((e: any) => e.plannedStart).length,
      })
    }).catch(() => {})
  }, [loaded, modules.badgeuse])

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })

  const ACTIONS = [
    { icon: '📅', label: 'Planning', color: 'bg-indigo-500', to: '/manager/planning' },
    ...(modules.badgeuse ? [{ icon: '🟢', label: 'Badges', color: 'bg-emerald-500', to: '/manager/badges', badge: clockStats.lateCount > 0 ? clockStats.lateCount : undefined }] : []),
    ...(modules.conges ? [{ icon: '🌴', label: 'Congés', color: 'bg-green-500', to: '/manager/absences', badge: counts.pendingAbsences }] : []),
    ...(modules.rh ? [{ icon: '📋', label: 'Feuilles', color: 'bg-purple-500', to: '/manager/timesheets', badge: counts.draftTimesheets }] : []),
    { icon: '👥', label: 'Équipe', color: 'bg-blue-500', to: '/manager/team' },
    ...(modules.conges || modules.rh ? [{ icon: '🔔', label: 'Alertes', color: 'bg-red-500', to: '/manager/absences', badge: counts.total > 0 ? counts.total : undefined }] : []),
  ]

  return (
    <ManagerMobileLayout>
      <div className="p-4 space-y-4">

        {/* Header card */}
        <div className="bg-indigo-700 rounded-2xl p-4 text-white">
          <div className="text-indigo-200 text-sm capitalize">{today}</div>
          {counts.total > 0 ? (
            <div className="mt-1">
              <div className="text-xl font-bold">{counts.total} action{counts.total > 1 ? 's' : ''} en attente</div>
              <div className="text-indigo-200 text-sm mt-0.5">
                {counts.pendingAbsences > 0 && `${counts.pendingAbsences} congé(s) · `}
                {counts.draftTimesheets > 0 && `${counts.draftTimesheets} feuille(s)`}
              </div>
            </div>
          ) : (
            <div className="text-xl font-bold mt-1">Tout est à jour</div>
          )}
        </div>

        {/* Presence summary */}
        {clockStats.shiftCount > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Présences aujourd'hui</h2>
              <button onClick={() => nav('/manager/badges')}
                className="text-xs text-indigo-600 font-medium hover:underline">
                Voir tout
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-green-50 rounded-xl p-2.5 text-center">
                <div className="text-xl font-bold text-green-600">{clockStats.presentCount}</div>
                <div className="text-xs text-green-500 mt-0.5">Présents</div>
              </div>
              <div className="bg-yellow-50 rounded-xl p-2.5 text-center">
                <div className="text-xl font-bold text-yellow-600">{clockStats.breakCount}</div>
                <div className="text-xs text-yellow-500 mt-0.5">En pause</div>
              </div>
              <div className={`rounded-xl p-2.5 text-center ${clockStats.lateCount > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                <div className={`text-xl font-bold ${clockStats.lateCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                  {clockStats.lateCount}
                </div>
                <div className={`text-xs mt-0.5 ${clockStats.lateCount > 0 ? 'text-amber-500' : 'text-gray-400'}`}>Retards</div>
              </div>
            </div>
          </div>
        )}

        {/* Signalement */}
        <button onClick={ouvrirSignalement}
          className="w-full flex items-center justify-center gap-2 bg-pink-600 active:bg-pink-700 text-white font-bold py-3.5 rounded-2xl shadow-sm">
          📸 Signaler un endroit à nettoyer
        </button>
        {signalOk && <p className="text-center text-sm text-green-700 font-medium">Signalement envoyé : la tâche part demain.</p>}
        {choixMagasin && (
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700">Quel magasin ?</p>
            {estabs.map(e => (
              <button key={e.id} onClick={() => { setChoixMagasin(false); setSignalEstab(e.id) }}
                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 font-medium active:bg-gray-50">
                {e.name}
              </button>
            ))}
            <button onClick={() => setChoixMagasin(false)} className="w-full text-sm text-gray-500 py-1">Annuler</button>
          </div>
        )}
        {signalEstab && (
          <SignalementModal establishmentId={signalEstab} onClose={() => setSignalEstab(null)}
            onDone={() => { setSignalEstab(null); setSignalOk(true) }} />
        )}

        {/* Quick actions */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Actions rapides</h2>
          <div className="grid grid-cols-3 gap-3">
            {ACTIONS.map(a => (
              <button key={a.to + a.label} onClick={() => nav(a.to)}
                className="relative flex flex-col items-center gap-2 p-3 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-95">
                {a.badge ? (
                  <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {a.badge}
                  </span>
                ) : null}
                <div className={`w-11 h-11 ${a.color} rounded-xl flex items-center justify-center text-xl`}>{a.icon}</div>
                <span className="text-xs text-gray-600 font-medium text-center leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </ManagerMobileLayout>
  )
}
